import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Typography from '../Text/Typography';
import Colors from '../Colors/Colors';
import Icon from '../Icon/Icon';
import IconButton from '../Button/IconButton';
import './MobileMenu.css';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onActiveLeveling?: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, onActiveLeveling }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      setIsVisible(false);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleActiveLeveling = () => {
    onClose();
    onActiveLeveling?.();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const navigationItems = [
    {
      section: 'Explore',
      items: [
        { label: 'Home', path: '/' },
        { label: 'Skincare Cabinet', path: '/skincarecabinet' },
        { label: 'Active Leveling', onClick: handleActiveLeveling },
      ],
    },
    {
      section: 'Learn More',
      items: [
        { label: 'About', path: '/learn/about' },
        { label: 'Contact', path: '/help' },
      ],
    },
  ];

  if (!isOpen) return null;

  return (
    <div
      className={`mobile-menu-backdrop ${isVisible ? 'visible' : ''}`}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={`mobile-menu ${isVisible ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <Typography variant="h2">Menu</Typography>
          <IconButton
            icon={<Icon name="close" size="small" />}
            onClick={onClose}
            ariaLabel="Close menu"
          />
        </div>

        <nav className="mobile-menu-nav">
          {navigationItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mobile-menu-section">
              <Colors variant="dark-gray">
                <Typography variant="body-small-bold" className="mobile-menu-section-header">
                  {section.section}
                </Typography>
              </Colors>
              <ul className="mobile-menu-list">
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="mobile-menu-item">
                    <button
                      type="button"
                      className={`mobile-menu-link ${
                        'path' in item && item.path && isActive(item.path) ? 'active' : ''
                      }`}
                      onClick={() => {
                        if ('onClick' in item && item.onClick) {
                          item.onClick();
                          return;
                        }
                        if ('path' in item && item.path) handleNavigation(item.path);
                      }}
                    >
                      <Colors variant="black">
                        <Typography variant="body-bold">{item.label}</Typography>
                      </Colors>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default MobileMenu;
